import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signOut, type Me } from '../api';
import { colourFor, initials } from '../format';
import { LanguageSwitcher } from './LanguageSwitcher';
import { setThemeMode, useThemeMode, type ThemeMode } from '../theme';

const MODES: ThemeMode[] = ['system', 'light', 'dark'];

/**
 * §23 M12.7 the avatar menu (the Microsoft 365 pattern): who you are, then Admin and Platform (only for those who
 * hold the role — the items aren't there otherwise), light / dark, language, sign out.
 */
export function UserMenu({ me }: { me: Me }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const mode = useThemeMode();
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !box.current?.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  // §23 M12.4 the same gate as before: tenant admins, and holders of a capability with its own Administration section
  const admin =
    me.isTenantAdmin ||
    me.capabilities.includes('manage_members') ||
    me.capabilities.includes('manage_branding');
  const role = [me.jobRole, me.organisation].filter(Boolean).join(' · ');
  return (
    <div className="user-menu" ref={box}>
      <button
        className="user-menu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('menu.open', { name: me.displayName })}
        title={me.displayName}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="avatar" style={{ background: colourFor(me.id) }}>
          {initials(me.displayName)}
        </span>
      </button>
      {open && (
        <div className="user-menu-panel" role="menu" aria-label={me.displayName}>
          <div className="user-menu-head">
            <span className="avatar avatar-lg" style={{ background: colourFor(me.id) }}>
              {initials(me.displayName)}
            </span>
            <span className="who">
              <strong>{me.displayName}</strong>
              {role && <span className="muted small">{role}</span>}
              <span className={`badge aff-${me.affiliation}`}>{t(`affiliation.${me.affiliation}`)}</span>
            </span>
          </div>
          <Link role="menuitem" className="user-menu-item" to="/profile" onClick={() => setOpen(false)}>
            {t('menu.profile')}
          </Link>
          <Link role="menuitem" className="user-menu-item" to="/shared" onClick={() => setOpen(false)}>
            {t('menu.shared')}
          </Link>
          {me.features.connected && (
            <Link role="menuitem" className="user-menu-item" to="/connected" onClick={() => setOpen(false)}>
              {t('menu.connected')}
            </Link>
          )}
          {admin && (
            <Link role="menuitem" className="user-menu-item" to="/admin" onClick={() => setOpen(false)}>
              <span aria-hidden>⚙</span> {t('administration.short')}
            </Link>
          )}
          <div className="user-menu-sep" />
          <div className="user-menu-row">
            <span id="menu-appearance">{t('menu.appearance')}</span>
            <div className="seg" role="radiogroup" aria-labelledby="menu-appearance">
              {MODES.map((m) => (
                <button
                  key={m}
                  role="radio"
                  aria-checked={mode === m}
                  className={mode === m ? 'on' : ''}
                  onClick={() => setThemeMode(m)}
                >
                  {t(`menu.mode.${m}`)}
                </button>
              ))}
            </div>
          </div>
          <label className="user-menu-row">
            <span>{t('nav.language')}</span>
            <LanguageSwitcher />
          </label>
          <div className="user-menu-sep" />
          <button role="menuitem" className="user-menu-item" onClick={() => void signOut()}>
            {t('nav.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}
